import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PdfDocument } from '@shared/models/pdf-document';
import { Router } from '@angular/router';
import { DisclosedDocumentDto } from '@shared/models/disclosed-document-dto';
import { EuiDialogComponent, EuiDialogConfig, EuiDialogService } from '@eui/components/eui-dialog';
import { DocumentService } from '../../../core/services/document.service';
import { CommissionDepartmentsEnum } from '@shared/enums/commission-department.enum';
import { DisclosureTypeEnum } from '@shared/enums/disclosure-type.enum';
import { EasePortalConstants } from '@shared/constants/ease-portal-constants';
import { ExceptionsInvolvedEnum } from '@shared/enums/exceptions-involved.enum';
import { DocumentDetailsDto } from '@shared/models/document-details-dto';
import { TranslateService } from '@ngx-translate/core';
import { selectConfigRegDocUrl } from '../../../core/selectors/config.selectors';
import { Store } from '@ngrx/store';
import { ConfigState } from '../../../core/reducers/config.reducers';
import { Observable } from 'rxjs';
import {
    downloadDisclosedDocument,
    downloadDisclosedDocumentsAsZip
} from '../../../core/actions/disclosed-document.actions';

@Component({
  standalone: false,
  selector: 'app-disclosed-document',
  templateUrl: './disclosed-document.component.html',
  styleUrls: ['./disclosed-document.component.scss']
})
export class DisclosedDocumentComponent implements OnInit {
    pdfList: PdfDocument[] = [];

    @Input()
    document: DisclosedDocumentDto;

    @Input()
    showOnlyDetails: boolean = false;

    @Output()
    redirect = new EventEmitter();

    @ViewChild('dialogPdf') dialogPdf: EuiDialogComponent;

    regDocUrl$: Observable<string>;

    finalRegDocUrl: string;

    /**
     * Is the modalId (to be opened) of each instance of this component
     */
    documentViewerModalId: string;

    /**
     * Is the id of the hrs Document that can be used to parse as zip a document
     * with multiple attachments
     */
    hrsDocumentIdToBeLoaded: string;

    /**
     * Is the id of hrs Item that can be used in combination with the hrsDocumentIdToBeLoaded
     * which is the parent document of this item to preview a specific attachment id pdf viewer
     */
    hrsItemIdToBeLoaded: string;

    /**
     * Is the id of ers Document to be loaded in order to preview it in search page
     */
    ersDocumentIdToBeLoaded: string;

    /**
     * Is the id of ers item to be loaded in order to preview it in search page
     */
    ersItemIdToBeLoaded: string;

    /**
     * Indicates the endpoint with the correct documentIds parameters
     * Two possible scenarios:
     *  - Ares: set hrsDocumentId and hrsItemId
     *  - Ers: set ersDocumentId
     */
    previewDocumentEndpoint: string;

    constructor(private router: Router,
                private store: Store<ConfigState>,
                public documentService: DocumentService,
                private translateService: TranslateService,
                private euiDialogService: EuiDialogService) { }

    ngOnInit() {
        this.documentViewerModalId = 'documentViewerModal' + this.document.publishedDocumentId;

        // If fetchFromRegDoc is true then we redirect to regDoc
        if (this.document.fetchFromRegDoc) {
            this.regDocUrl$ = this.store.select(selectConfigRegDocUrl);
        }
    }

    /**
     * Util methods to show CommissionDepartmentsEnum, DisclosureTypeEnum
     */
    public get commissionDepartmentsEnum(): typeof CommissionDepartmentsEnum {
        return CommissionDepartmentsEnum;
    }

    public get disclosureTypeEnum(): typeof DisclosureTypeEnum {
        return DisclosureTypeEnum;
    }

    public get exceptionsInvolvedEnum(): typeof ExceptionsInvolvedEnum {
        return ExceptionsInvolvedEnum;
    }

    /**
     * Create the url of preview endpoint that is called from previewer component
     */
    createPreviewDocumentEndpoint(hrsDocumentId: string,
                                  hrsItemId: string,
                                  ersDocumentId: string,
                                  ersItemId: string,
                                  attachmentsLength: number) {

        if (this.document.fetchFromAres) {
            this.hrsDocumentIdToBeLoaded = hrsDocumentId;
            this.hrsItemIdToBeLoaded = hrsItemId;
            this.previewDocumentEndpoint = 'api/portal/externalizedDocument/hrs/' + this.hrsDocumentIdToBeLoaded
                + '/' + this.hrsItemIdToBeLoaded + '/' + this.document.isAdHocDocument + '/download';
        } else {
            this.ersDocumentIdToBeLoaded = ersDocumentId;
            this.ersItemIdToBeLoaded = ersItemId;
            if (attachmentsLength > 1) {
                this.previewDocumentEndpoint = 'api/portal/externalizedDocument/ers/' + this.ersDocumentIdToBeLoaded
                    + '/' + this.ersItemIdToBeLoaded + '/' + this.document.isAdHocDocument + '/preview';
            } else {
                this.previewDocumentEndpoint = 'api/portal/externalizedDocument/ers/' + this.ersDocumentIdToBeLoaded
                    + '/' + this.document.isAdHocDocument + '/download';
            }
        }
    }

    navigateToDocumentDetails(document: DisclosedDocumentDto, regDocUrl: string) {
        if (document.hasCases) {
            this.redirect.emit();
            if (!this.document.fetchFromRegDoc) {
                this.router.navigateByUrl('search/document-details/' + document.publishedDocumentId);
            } else {
                window.location.href = this.createRegDocUrl(regDocUrl);
            }
        }
    }

    downloadDisclosedDocument(document: DisclosedDocumentDto) {
        this.store.dispatch(downloadDisclosedDocument({
            attachment: document.attachedDocuments[0],
            disclosedDocument: document
        }));
    }

   downloadDisclosedDocumentAsZip(document: DisclosedDocumentDto) {
       this.store.dispatch(downloadDisclosedDocumentsAsZip({
           attachments: document.attachedDocuments,
           disclosedDocument: document
       }));
    }

    previewDocument(document: DisclosedDocumentDto) {

        // If we are going to preview a document from ers (DECIDE, OTHER)
        // and this document has multiple attached documents we need to have
        // access to these metadata, so we fetch metadata of the attachedDocuments
        // from proxy with the call below.
        // Otherwise, we load on init the first attachment and here just open the modal
        if (document.ersDocumentId && document.numFiles > 1) {
            this.documentService.getErsDocumentAttachmentsMetadata(document.ersDocumentId, document.isAdHocDocument)
                .subscribe({
                    next: (documentDetails: DocumentDetailsDto[]) => {
                        this.document = {
                            ...this.document,
                            attachedDocuments: documentDetails
                        };
                    },
                    error: () => {
                    },
                    complete: () => {
                        this.displayMultiplePDFs(this.document.attachedDocuments, this.document.isAdHocDocument);
                        this.dialogPdf.openDialog();

                    }
                });
        } else {

            // if fetchFromAres is true we fetch document from ares, using the hrs endpoint
            if (this.document.fetchFromAres) {
                this.hrsDocumentIdToBeLoaded = this.document.attachedDocuments[0]?.hrsDocumentId;
                this.hrsItemIdToBeLoaded = this.document.attachedDocuments[0]?.hrsItemId;
                this.previewDocumentEndpoint = 'api/portal/externalizedDocument/hrs/' + this.hrsDocumentIdToBeLoaded
                    + '/' + this.hrsItemIdToBeLoaded + '/' + document.isAdHocDocument + '/download';
                this.displaySinglePDF({
                    title: this.document.documentTitle,
                    url: this.previewDocumentEndpoint
                });
            } else {
                // Decide/Other with one attached document
                this.ersDocumentIdToBeLoaded = this.document.ersDocumentId;
                this.previewDocumentEndpoint = 'api/portal/externalizedDocument/ers/' + this.ersDocumentIdToBeLoaded +
                    '/' + this.document.isAdHocDocument + '/download';
                this.displaySinglePDF({
                    title: this.document.documentTitle,
                    url: this.previewDocumentEndpoint
                });
            }

            this.dialogPdf.openDialog();
        }

    }

    displayMultiplePDFs(documents: DocumentDetailsDto[], isAdHocDocument: boolean) {

        this.pdfList = [];
        documents.map((document: DocumentDetailsDto) => {
            if(document.ersItemId) {
                this.pdfList.push(PdfDocument.of({
                        title: document.title,
                        url: 'api/portal/externalizedDocument/ers/' + document.ersDocumentId
                            + '/' + document.ersItemId + '/' + isAdHocDocument + '/preview'
                    })
                );
            } else {
                this.displayAlertForEmptyErsItemId(document);
            }
        });
        return this.pdfList;
    }

    displaySinglePDF(pdfValue: Record<string, string>) {
        this.clearPDFList();
        this.pdfList.push(PdfDocument.of(pdfValue));
        return this.pdfList[0];
    }

    clearPDFList() {
        this.pdfList = [];
    }

    /**
     * When an item from the left list is clicked this method is triggered
     * @param event the sectionElement that is the document
     */
    onItemClick(event, attachmentsLength) {
        event.preventDefault();
        this.createPreviewDocumentEndpoint(event.sectionElement.hrsDocumentId, event.sectionElement.hrsItemId,
            event.sectionElement.ersDocumentId, event.sectionElement.ersItemId, attachmentsLength);
    }

    protected readonly DisclosureTypeEnum = DisclosureTypeEnum;

    private displayAlertForEmptyErsItemId(document: DocumentDetailsDto) {

        const config = new EuiDialogConfig({
            title: 'Warning',
            content: 'The document ' + document.title + ' could not open due to missing ersItemId. Please contact your support team.',
            open: () => {
                console.log('The pdf ' + document.title + ' could not open due to missing ersItemId.');
            }
        });
        this.euiDialogService.openDialog(config);
    }

    /**
     * Create the url of regDoc where a document is located
     */
    private createRegDocUrl(url: string): string {
        const regDocUrl = url.replace(EasePortalConstants.DOC_REFERENCE_PARAMETER, this.document.referenceNumber);
        this.finalRegDocUrl = regDocUrl.replace(EasePortalConstants.SESSION_LANGUAGE_PARAMETER, this.translateService.currentLang);
        return this.finalRegDocUrl;
    }
}
